import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../../components/bulk-selection/index.js";
import { Pagination } from "../../../components/ui/pagination.js";
import { BulkDeleteDialog } from "../../../components/ui/bulk-delete-dialog.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { usePagedList } from "../../../hooks/usePagedList.js";
import { marketingApi } from "../client.js";
import { PlatformPill, SectionCardHeader, draftDisplayTitle } from "../shared.js";
import type { Draft } from "../types.js";
import { DraftReviewDialog } from "./DraftReviewDialog.js";
import { AutoGenerateToggle } from "./pending-approval/AutoGenerateToggle.js";
import { deleteErrorDetail } from "./pending-approval/deleteErrorDetail.js";
import { DraftRow } from "./pending-approval/DraftRow.js";
import { HeadCell } from "./pending-approval/HeadCell.js";

const QUEUE_KEY = ["marketing", "queue"] as const;

/**
 * Left-side table of drafts waiting on operator approval. Each row is one
 * draft; clicking the row opens the `DraftReviewDialog` modal where the full
 * content + image + sources + Approve/Deny/Edit/Re-generate actions live.
 *
 * Rows also carry a selection checkbox and a trash button so operators can
 * clear the queue fast without opening each draft. Delete is a **hard delete**;
 * both the per-row and bulk paths route through the shared confirm dialogs and
 * are OPTIMISTIC with a destructive toast on failure (the old silent-fail bug).
 * The queue paginates client-side at 25/page.
 *
 * This file is the orchestrator: the review-queue + agent-settings queries, the
 * delete-one / bulk-delete / auto-draft mutations, and the table assembly. The
 * row lives in `pending-approval/DraftRow`, the header cell in `HeadCell`, the
 * auto-generate switch in `AutoGenerateToggle`, and the failure-reason helper
 * in `deleteErrorDetail`.
 */
export function PendingApprovalTable(): JSX.Element {
  const queue = useQuery({
    queryKey: QUEUE_KEY,
    // Wrap (don't pass the method ref) so react-query's QueryFunctionContext
    // isn't bound to the new `limit` arg → `?limit=[object Object]`. Default
    // limit pulls the whole pending backlog.
    queryFn: () => marketingApi.reviewQueue(),
    refetchInterval: 15_000,
    retry: 0,
  });
  const qc = useQueryClient();
  // Delete is destructive — gate it on `marketing:edit` like the header
  // controls (Steer / Mode / Pause) and the review actions. Read-only
  // viewers get the table without checkboxes / trash / bulk bar; clicking
  // a row still opens the (read-only) review dialog. Hidden, not disabled.
  const canEdit = useCan("marketing:edit");
  // Turning autonomous drafting on/off is "Manage Marketing settings".
  const canManage = useCan("marketing:manage");

  // The auto-generate switch lives here too (not just in Settings) so the
  // operator can stop the agent flooding this timeline with auto-drafts right
  // where they see it happening.
  const settings = useQuery({
    queryKey: ["marketing", "agent-settings"],
    queryFn: marketingApi.getAgentSettings,
    retry: 0,
  });
  const fullyAuto = settings.data?.autonomy === "fully_auto";
  const autoOn = !!settings.data?.auto_draft_enabled || fullyAuto;

  const [activeId, setActiveId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const drafts = queue.data ?? [];
  const active = drafts.find((d) => d.id === activeId) ?? null;

  const {
    paged,
    page,
    perPage,
    totalCount,
    totalPages,
    setPage,
    setPerPage,
  } = usePagedList(drafts, 25);

  const bulk = useBulkSelection(paged);
  const selectedDrafts = drafts.filter((d) => bulk.selected.has(d.id));

  // Both delete paths are OPTIMISTIC (the row vanishes immediately) and —
  // critically — surface a destructive toast on failure. The old versions did
  // neither: a failed DELETE (e.g. the marketing app returning 404 because the
  // box runs an older image, or 502 / unreachable) threw, the ConfirmDialog
  // swallowed the throw to stay open, and NOTHING told the operator — the
  // "I click delete and nothing happens" silent-fail bug. Now a failure rolls
  // the row back AND says why; a success confirms with a toast. `onSettled`
  // always reconciles against the server so the queue can't drift.
  const deleteOne = useMutation({
    mutationFn: (id: number) => marketingApi.deleteDraft(id),
    onMutate: async (id: number) => {
      await qc.cancelQueries({ queryKey: QUEUE_KEY });
      const prev = qc.getQueryData<Draft[]>(QUEUE_KEY);
      qc.setQueryData<Draft[]>(QUEUE_KEY, (old) =>
        (old ?? []).filter((d) => d.id !== id),
      );
      return { prev };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(QUEUE_KEY, ctx.prev);
      toast({
        title: "Couldn't delete the draft",
        description: deleteErrorDetail(err),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({ title: "Draft deleted" });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });
  const autoDraft = useMutation({
    mutationFn: (auto_draft_enabled: boolean) =>
      marketingApi.patchAgentSettings({ auto_draft_enabled }),
    onSuccess: (next) => {
      qc.setQueryData(["marketing", "agent-settings"], next);
      toast({
        title: next.auto_draft_enabled
          ? "Auto-generated drafts on"
          : "Auto-generated drafts off",
      });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't update",
        description: e instanceof ApiError ? e.message : "Please retry.",
      }),
  });
  const deleteMany = useMutation({
    mutationFn: (ids: number[]) => marketingApi.bulkDeleteDrafts(ids),
    onMutate: async (ids: number[]) => {
      await qc.cancelQueries({ queryKey: QUEUE_KEY });
      const prev = qc.getQueryData<Draft[]>(QUEUE_KEY);
      const dropping = new Set(ids);
      qc.setQueryData<Draft[]>(QUEUE_KEY, (old) =>
        (old ?? []).filter((d) => !dropping.has(d.id)),
      );
      return { prev };
    },
    onError: (err, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(QUEUE_KEY, ctx.prev);
      toast({
        title: "Couldn't delete the drafts",
        description: deleteErrorDetail(err),
        variant: "destructive",
      });
    },
    onSuccess: (res, ids) => {
      bulk.clear();
      const n = res.deleted_ids?.length ?? ids.length;
      toast({ title: `Deleted ${n} draft${n === 1 ? "" : "s"}` });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });

  return (
    <Card>
      <SectionCardHeader
        title="Pending approval"
        subtitle={
          queue.isLoading
            ? "Loading…"
            : totalCount === 0
              ? "Nothing waiting"
              : `${totalCount} item${totalCount === 1 ? "" : "s"} — everything the agent generated (auto, ideas, Create New), newest first`
        }
        trailing={
          canManage && settings.data ? (
            <AutoGenerateToggle
              fullyAuto={fullyAuto}
              autoOn={autoOn}
              pending={autoDraft.isPending}
              onToggle={(checked) => autoDraft.mutate(checked)}
            />
          ) : undefined
        }
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {queue.isLoading ? (
          <Box
            sx={{
              display: "flex",
              height: 220,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "text.secondary",
            }}
            role="status"
            aria-live="polite"
            data-testid="pending-approval-loading"
          >
            <CircularProgress size={24} aria-hidden="true" />
            <Typography sx={{ fontSize: 14 }}>Loading drafts…</Typography>
          </Box>
        ) : totalCount === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            The queue is empty. The agent will keep watching for trends — new
            drafts arrive automatically.
          </Typography>
        ) : (
          <>
            {canEdit && (
              <BulkActionBar count={bulk.count} onClear={bulk.clear}>
                <BulkButton
                  variant="destructive"
                  disabled={deleteMany.isPending}
                  onClick={() => setBulkOpen(true)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  Delete {bulk.count}
                </BulkButton>
              </BulkActionBar>
            )}
            <Box sx={{ mx: -0.5, overflowX: "auto" }} data-testid="pending-approval">
              <Table size="small" sx={{ minWidth: "100%" }}>
                <TableHead>
                  <TableRow>
                    {canEdit && (
                      <HeadCell sx={{ width: 32 }}>
                        <BulkCheckbox
                          checked={bulk.allOnPageChecked}
                          indeterminate={bulk.someOnPageChecked}
                          onChange={(checked) => bulk.toggleAllOnPage(checked)}
                          ariaLabel="Select all drafts on this page"
                        />
                      </HeadCell>
                    )}
                    <HeadCell>Platform</HeadCell>
                    <HeadCell>Title</HeadCell>
                    <HeadCell align="right">Age</HeadCell>
                    {canEdit && (
                      <HeadCell sx={{ width: 32 }}>
                        <Box component="span" sx={{ position: "absolute", width: 1, height: 1, p: 0, m: -0.125, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}>
                          Actions
                        </Box>
                      </HeadCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((d) => (
                    <DraftRow
                      key={d.id}
                      draft={d}
                      canEdit={canEdit}
                      checked={bulk.isSelected(d.id)}
                      onToggle={(checked, opts) =>
                        bulk.setChecked(d.id, checked, { shiftKey: opts.shiftKey })
                      }
                      onOpen={() => setActiveId(d.id)}
                      onDelete={() => setConfirmId(d.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Pagination
              page={page}
              perPage={perPage}
              totalCount={totalCount}
              totalPages={totalPages}
              entityNoun="drafts"
              onPageChange={setPage}
              onPerPageChange={setPerPage}
            />
          </>
        )}
      </CardContent>

      <DraftReviewDialog
        draft={active}
        open={active !== null}
        onClose={() => setActiveId(null)}
      />

      <ConfirmDialog
        isOpen={confirmId !== null}
        onClose={() => setConfirmId(null)}
        title="Delete this draft?"
        description="This permanently removes the draft and its review history. This can't be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (confirmId !== null) await deleteOne.mutateAsync(confirmId);
        }}
      />

      <BulkDeleteDialog
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        items={selectedDrafts}
        entityLabel={{ singular: "draft", plural: "drafts" }}
        cascadingWarning="This permanently removes the selected drafts and their review history. This can't be undone."
        renderItem={(d) => draftDisplayTitle(d)}
        renderItemBadge={(d) => <PlatformPill platform={d.platform} />}
        onConfirm={async (ids) => {
          await deleteMany.mutateAsync(ids.map(Number));
        }}
      />
    </Card>
  );
}
